import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Menu, X, Code2, Sparkles, Send } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useSpring, useReducedMotion } from 'framer-motion';
import { MagneticButton } from './animations/MagneticButton';
import { EASINGS } from '../lib/motion';
import { IS_PRERENDER } from '../lib/prerender';
import { scrollToElement, scrollToTop } from '../lib/scroll';

const ContactModal = lazy(() => import('./ContactModal'));

function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [isContactOpen, setIsContactOpen] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 40) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }

            // Simple section spy
            const sections = ['home', 'projects', 'skills', 'services', 'about', 'articles', 'contact'];
            const scrollPos = window.scrollY + 100;
            for (const section of sections) {
                const el = document.getElementById(section);
                if (el) {
                    const top = el.offsetTop;
                    const height = el.offsetHeight;
                    if (scrollPos >= top && scrollPos < top + height) {
                        setActiveSection(section);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        scrollToElement(document.getElementById(sectionId), { offset: 80 });
        setIsMenuOpen(false);
        setActiveSection(sectionId);
    };

    const navLinks = [
        { id: 'home', label: 'Home' },
        { id: 'projects', label: 'Projects' },
        { id: 'skills', label: 'Skills' },
        { id: 'services', label: 'Services' },
        { id: 'about', label: 'Experience' },
        { id: 'articles', label: 'Articles' },
    ];

    const handleLogoClick = () => {
        scrollToTop();
        setActiveSection('home');
    };

    return (
        <motion.header
            initial={prefersReducedMotion || IS_PRERENDER ? false : { opacity: 0, y: -20 }}
            animate={prefersReducedMotion || IS_PRERENDER ? false : { opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASINGS.smooth, delay: 0.3 }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 will-change-transform ${
                scrolled
                    ? 'bg-[#090D16]/85 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/40 py-3'
                    : 'bg-transparent border-b border-transparent py-5'
            }`}
        >
            {/* Scroll progress indicator */}
            <motion.div
                aria-hidden="true"
                className="absolute top-0 left-0 right-0 h-0.5 origin-left bg-gradient-to-r from-indigo-500 via-cyan-400 to-indigo-500"
                style={{ scaleX: progress }}
            />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
                {/* Brand Logo */}
                <div
                    onClick={handleLogoClick}
                    className="flex items-center gap-2.5 cursor-pointer group"
                >
                    <motion.div
                        whileHover={{ scale: 1.08, rotate: -4 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                        className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 via-indigo-500 to-cyan-400 p-[1px] shadow-lg shadow-indigo-500/20"
                        style={{ willChange: 'transform' }}
                    >
                        <div className="w-full h-full bg-[#090D16] rounded-[11px] flex items-center justify-center">
                            <Code2 className="w-5 h-5 text-indigo-400 group-hover:text-cyan-300 transition-colors" />
                        </div>
                    </motion.div>
                    <div>
                        <span className="font-sora font-bold text-lg text-white tracking-tight group-hover:text-indigo-300 transition-colors">
                            Jerwin<span className="text-indigo-500">.dev</span>
                        </span>
                        <span className="block text-[10px] text-slate-400 uppercase tracking-widest font-medium">
                            Fullstack & AI
                        </span>
                    </div>
                </div>

                {/* Desktop Links with gliding active indicator */}
                <nav className="hidden md:flex items-center gap-1 bg-white/[0.03] border border-white/[0.08] backdrop-blur-md px-4 py-1.5 rounded-full shadow-inner">
                    {navLinks.map((link) => {
                        const isActive = activeSection === link.id;
                        return (
                            <MagneticButton key={link.id} strength={0.18}>
                                <button
                                    onClick={() => scrollToSection(link.id)}
                                    className={`relative px-4 py-1.5 text-xs font-medium rounded-full ${
                                        isActive
                                            ? 'text-white'
                                            : 'text-slate-300 hover:text-white hover:bg-white/5'
                                    } transition-colors duration-300`}
                                >
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-active-pill"
                                            className="absolute inset-0 bg-indigo-600 rounded-full shadow-md shadow-indigo-500/30"
                                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                    <span className="relative z-10">{link.label}</span>
                                </button>
                            </MagneticButton>
                        );
                    })}
                </nav>

                {/* CTA Contact Button */}
                <div className="hidden md:flex items-center gap-3">
                    <button
                        onClick={() => setIsContactOpen(true)}
                        className="relative group overflow-hidden rounded-full p-[1px] focus:outline-none"
                    >
                        <span className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full group-hover:opacity-90 transition-opacity"></span>
                        <motion.span
                            whileHover={{ scale: 1.04 }}
                            transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                            className="relative px-5 py-2 rounded-full bg-[#090D16] text-xs font-semibold text-white flex items-center gap-2 group-hover:bg-transparent transition-colors duration-300 will-change-transform"
                        >
                            <span>Get In Touch</span>
                            <Send className="w-3.5 h-3.5 text-cyan-400 group-hover:translate-x-0.5 transition-transform" />
                        </motion.span>
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="md:hidden p-2 rounded-lg bg-white/5 border border-white/10 text-slate-200 hover:text-white focus:outline-none"
                    aria-label="Toggle Navigation Menu"
                >
                    {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="md:hidden bg-[#0F172A]/95 backdrop-blur-xl border-b border-white/10 px-6 py-6 space-y-3 overflow-hidden"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.id}
                                onClick={() => scrollToSection(link.id)}
                                className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    activeSection === link.id
                                        ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30'
                                        : 'text-slate-300 hover:bg-white/5 hover:text-white'
                                }`}
                            >
                                {link.label}
                            </button>
                        ))}
                        <div className="pt-4 border-t border-white/10">
                            <motion.button
                                whileTap={{ scale: 0.97 }}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    setIsContactOpen(true);
                                }}
                                className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-indigo-600 to-cyan-600 text-white font-medium text-sm flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20"
                            >
                                <Sparkles className="w-4 h-4" />
                                <span>Contact Me</span>
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Contact Modal (lazy-loaded on first open) */}
            <Suspense fallback={null}>
                {isContactOpen && (
                    <ContactModal open={isContactOpen} onClose={() => setIsContactOpen(false)} />
                )}
            </Suspense>
        </motion.header>
    );
}

export default Nav;